import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { bankCardMetadata } from "@features/Budget/budgetConstants";
import { Button } from "@components/Button";
import { DummyRadio } from "@components/inputs/Radio/DummyRadio";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useSubmitUnderpermissionedExistingConsentMutation } from "@services/powersOfAttorney/mutations";
import { type IUnderpermissionedExistingConsents } from "@services/powersOfAttorney/types";
import { formatDate } from "@utils/units";

interface IPowerOfAttorneyAddFromBudgetConsentModalProps
  extends IUseModalProps {
  consents: IUnderpermissionedExistingConsents;
  onSuccess?: (powerOfAttorneyLedgerConsentId: string) => void;
}

export const PowerOfAttorneyAddFromBudgetConsentModal: React.FC<
  IPowerOfAttorneyAddFromBudgetConsentModalProps
> = (props) => {
  const { consents, onSuccess, ...rest } = props;
  const modal = useModal(rest);
  const { t } = useTranslation();
  const [selectedledgerConsentId, setSelectedLedgerConsentId] = useState<
    number | undefined
  >(consents.banks[0]?.consents[0]?.ledgerConsentId);

  const { mutateAsync, isPending } =
    useSubmitUnderpermissionedExistingConsentMutation();

  const { toastApiError } = useApiErrorUtils();

  const onSubmit = async () => {
    if (selectedledgerConsentId === undefined) {
      return;
    }

    const data = await mutateAsync({
      ledgerConsentId: selectedledgerConsentId,
    }).catch(toastApiError);

    if (!data) {
      return;
    }

    onSuccess?.(data.powerOfAttorneyLedgerConsentId);
    modal.close();
  };

  return (
    <Modal
      modal={modal}
      title={t("powerOfAttorney.addFromBudgetConsent.title")}
      subtitle={t("powerOfAttorney.addFromBudgetConsent.subtitle")}
      className="md:max-w-3xl"
      actionButtons={
        <React.Fragment>
          <Button variant="secondary" onClick={modal.close} className="w-full">
            {t("common.form.buttons.cancel")}
          </Button>
          <Button className="w-full" onClick={onSubmit} isLoading={isPending}>
            {t("common.form.buttons.continue")}
          </Button>
        </React.Fragment>
      }
    >
      <div className="flex flex-col gap-6">
        {consents.banks.map((bank) => {
          const bankMetadata = bankCardMetadata[bank.bankName];

          return (
            <div className="flex flex-col gap-6 divide-y divide-grey-light rounded-2xl border border-grey-light p-6">
              <div className="flex items-center gap-4">
                {bankMetadata.icon && <bankMetadata.icon className="size-12" />}
                <span className="text-sm font-medium">{bank.bankName}</span>
              </div>
              <div className="flex flex-col gap-6 pt-6">
                {bank.consents.map((consent) => (
                  <React.Fragment>
                    <div className="flex items-center justify-between">
                      <span className="font-mediumtext-sm">
                        <Trans
                          i18nKey="powerOfAttorney.addFromBudgetConsent.activeAccountsValidUntil"
                          values={{
                            date: formatDate(
                              consent.validUntil,
                              t("common.dateFormat.yearMonthDay"),
                            ),
                          }}
                          components={{
                            b: <b />,
                          }}
                        />
                      </span>
                      <div>
                        <DummyRadio
                          checked={
                            selectedledgerConsentId === consent.ledgerConsentId
                          }
                          onClick={() =>
                            setSelectedLedgerConsentId(consent.ledgerConsentId)
                          }
                        />
                      </div>
                    </div>
                    {consent.bankAccounts.map((account) => (
                      <div className="rounded-2xl border border-grey-light p-6">
                        <span className="text-sm font-medium">
                          {account.iban}
                        </span>
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};
